import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { Button } from '../../ui/button';
import { ArrowRight } from 'lucide-react';
import { REGIONAL_ENVIRONMENTS, setSelectedRegion, setCustomAppUrl, getRegionalEnvironment, createCustomFallbackConfig, } from '../../lib/service-discovery';
/**
 * Normalize a URL by adding https:// if no protocol is present
 */
function normalizeUrl(url) {
    const trimmed = url.trim();
    // If it already has a protocol, return as-is
    if (/^https?:\/\//i.test(trimmed)) {
        return trimmed;
    }
    // Add https:// prefix
    return `https://${trimmed}`;
}
/**
 * Validate a custom URL
 * Returns an error message if invalid, or null if valid
 */
function validateCustomUrl(url) {
    if (!url.trim()) {
        return 'URL is required';
    }
    try {
        const normalized = normalizeUrl(url);
        const parsed = new URL(normalized);
        // Must be HTTPS (except localhost for development)
        if (parsed.protocol !== 'https:' && parsed.hostname !== 'localhost') {
            return 'URL must use HTTPS';
        }
        // Must have a valid hostname
        if (!parsed.hostname || parsed.hostname.length < 3) {
            return 'Invalid hostname';
        }
        return null;
    }
    catch {
        return 'Invalid URL format. Please enter a valid domain (e.g., airia.yourcompany.com)';
    }
}
function EnvironmentSelection({ onEnvironmentSelect, hostType }) {
    const [selectedEnvironment, setSelectedEnvironmentLocal] = useState('');
    const [customUrl, setCustomUrl] = useState('');
    const [urlError, setUrlError] = useState(null);
    const getDescription = () => {
        switch (hostType) {
            case 'outlook':
                return 'Boost your email productivity with AI-powered drafting, summarization, and intelligent responses.';
            case 'powerpoint':
                return 'Enhance your presentations with AI-powered analysis, content suggestions, and intelligent slide generation.';
            default:
                return 'Enhance your documents with AI-powered analysis, writing assistance, and intelligent content generation.';
        }
    };
    const getTaskpanePath = () => {
        switch (hostType) {
            case 'outlook':
                return '/taskpane-outlook.html';
            case 'powerpoint':
                return '/taskpane-powerpoint.html';
            default:
                return '/taskpane.html';
        }
    };
    const handleCustomUrlChange = (value) => {
        setCustomUrl(value);
        // Clear error when user starts typing
        if (urlError) {
            setUrlError(null);
        }
    };
    const handleSubmit = (e) => {
        e.preventDefault();
        if (!selectedEnvironment)
            return;
        // Determine the target office URL and environment params for the redirect
        let targetOfficeUrl;
        let redirectParams;
        if (selectedEnvironment === 'custom') {
            // Validate custom URL
            const error = validateCustomUrl(customUrl);
            if (error) {
                setUrlError(error);
                return;
            }
            // Normalize URL (add https:// if needed, remove trailing slash)
            const normalizedUrl = normalizeUrl(customUrl).replace(/\/$/, '');
            // Get the office URL from the custom URL fallback config
            const fallbackConfig = createCustomFallbackConfig(normalizedUrl);
            targetOfficeUrl = fallbackConfig.fallbackOfficeUrl;
            // Save custom URL to localStorage (will be available after redirect if same origin,
            // or restored from URL params if different origin)
            setCustomAppUrl(normalizedUrl);
            // Set up redirect params
            redirectParams = new URLSearchParams();
            redirectParams.set('_customUrl', normalizedUrl);
        }
        else {
            // Get the regional environment config
            const regionalEnv = getRegionalEnvironment(selectedEnvironment);
            if (!regionalEnv) {
                console.error('[EnvironmentSelection] Unknown region:', selectedEnvironment);
                return;
            }
            targetOfficeUrl = regionalEnv.fallbackOfficeUrl;
            // Save selected region to localStorage
            setSelectedRegion(selectedEnvironment);
            // Set up redirect params
            redirectParams = new URLSearchParams();
            redirectParams.set('_region', selectedEnvironment);
        }
        // Determine the taskpane path based on host type
        const taskpanePath = getTaskpanePath();
        // Check if we're already on the correct origin
        const targetOrigin = new URL(targetOfficeUrl).origin;
        const currentOrigin = window.location.origin;
        if (currentOrigin === targetOrigin) {
            // Already on the correct origin, just notify parent to reload
            console.log('[EnvironmentSelection] Already on correct origin, notifying parent...');
            onEnvironmentSelect();
        }
        else {
            // Redirect to the correct office URL with environment params
            const redirectUrl = new URL(taskpanePath, targetOfficeUrl);
            redirectUrl.search = redirectParams.toString();
            console.log('[EnvironmentSelection] Redirecting to:', redirectUrl.toString());
            window.location.href = redirectUrl.toString();
        }
    };
    return (_jsxs("div", { className: "flex min-h-screen bg-white", children: [_jsx("div", { className: "flex-1 flex flex-col justify-center items-center px-6", children: _jsxs("div", { className: "w-full max-w-md", children: [_jsx("div", { className: "mb-8", children: _jsx("img", { src: "/assets/airia-logo-full.svg", alt: "Airia logo", className: "w-[121px] h-[52px]" }) }), _jsx("div", { className: "text-3xl font-semibold mb-4 text-center text-black", children: "Welcome to Airia" }), _jsx("p", { className: "text-sm text-gray-600 text-center mb-8", children: getDescription() }), _jsxs("form", { className: "space-y-6", onSubmit: handleSubmit, children: [_jsxs("div", { className: "space-y-2", children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 text-center", children: "Select your login region" }), _jsxs("select", { className: "w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-black", value: selectedEnvironment, onChange: (e) => setSelectedEnvironmentLocal(e.target.value), children: [_jsx("option", { value: "", children: "Choose a region" }), REGIONAL_ENVIRONMENTS.map((env) => (_jsx("option", { value: env.region, children: env.name }, env.region))), _jsx("option", { value: "custom", children: "Custom URL" })] })] }), selectedEnvironment === 'custom' && (_jsxs("div", { className: "space-y-2", children: [_jsx("label", { className: "block text-sm font-medium text-gray-700", children: "Custom URL" }), _jsx("input", { type: "text", className: `w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-black ${urlError ? 'border-red-500' : 'border-gray-300'}`, placeholder: "airia.yourcompany.com", value: customUrl, onChange: (e) => handleCustomUrlChange(e.target.value), required: true }), urlError ? (_jsx("p", { className: "text-xs text-red-500", children: urlError })) : (_jsx("p", { className: "text-xs text-gray-500", children: "Enter the domain for your Airia instance (e.g., airia.yourcompany.com)" }))] })), _jsxs(Button, { type: "submit", className: "w-full bg-indigo-600 hover:bg-indigo-700 text-white py-2 px-4 rounded-md flex items-center justify-center space-x-2", disabled: !selectedEnvironment ||
                                        (selectedEnvironment === 'custom' && !customUrl), children: [_jsx("span", { children: "Continue" }), _jsx(ArrowRight, { className: "h-5 w-5" })] })] }), _jsxs("p", { className: "text-sm text-gray-600 text-center mt-6", children: ["Don't have an account?", ' ', _jsx("a", { href: "https://airia.com/register/", target: "_blank", rel: "noopener noreferrer", className: "text-indigo-600 hover:text-indigo-700 font-medium", children: "Sign Up" })] })] }) }), _jsx("div", { className: "hidden lg:block lg:flex-1", children: _jsx("img", { className: "w-full h-screen object-cover", src: "/assets/airia-addon-bg.png", alt: "Airia background" }) })] }));
}
export default EnvironmentSelection;
